// pages/LandingPage.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Shield, Newspaper, Menu, X, ArrowUpRight } from 'lucide-react';
import { ContactModal } from '../components/ContactModal';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { BASE_URL } from '../constants';

export const LandingPage = ({ onLoginClick }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeModal, setActiveModal] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const heroVideoRef = useRef(null);

  const [form, setForm] = useState({ name: '', company: '', position: '', phone: '', email: '', question: '' });
  const [formErrors, setFormErrors] = useState({});
  const [formStatus, setFormStatus] = useState('idle');
  const [formServerError, setFormServerError] = useState('');

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const validateForm = () => {
    const e = {};
    if (!form.name.trim()) e.name = t('LandingPage.enter_name');
    if (!form.phone.trim()) {
      e.phone = t('LandingPage.enter_number_phone');
    } else {
      const digits = form.phone.replace(/\D/g, '');
      if (digits.length < 11 || digits.length > 12) e.phone = t('LandingPage.invalid_number');
    }
    if (!form.email.trim()) e.email = t('LandingPage.enter_email');
    else if (!/\S+@\S+\.\S+/.test(form.email.trim())) e.email = t('LandingPage.invalid_email');
    return e;
  };

  const handleFormSubmit = async () => {
    const e = validateForm();
    if (Object.keys(e).length > 0) { setFormErrors(e); return; }
    setFormStatus('loading');
    setFormServerError('');
    try {
      const res = await fetch(`${BASE_URL}/api/consultation/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          org_name: form.company.trim() || form.name.trim(),
          email: form.email.trim(),
          name: form.name.trim(),
          position: form.position.trim(),
          phone: form.phone.trim(),
          question: form.question.trim(),
          message: [
            form.name.trim() && t('LandingPage.name_2', { value: form.name.trim() }),
            form.position.trim() && t('LandingPage.position_2', { value: form.position.trim() }),
            form.phone.trim() && t('LandingPage.phone', { value: form.phone.trim() }),
          ].filter(Boolean).join('\n') || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || t('LandingPage.error_servera'));
      setFormStatus('success');
    } catch (err) {
      setFormServerError(err.message);
      setFormStatus('error');
    }
  };

  const scrollToConsultation = () => {
    document.getElementById('consultation')?.scrollIntoView({ behavior: 'smooth' });
  };

  const [heroVisible, setHeroVisible] = useState(true);
  const [formVisible, setFormVisible] = useState(false);
  const showFab = !heroVisible && !formVisible;

  React.useEffect(() => {
    const heroEl = document.getElementById('hero');
    const formEl = document.getElementById('consultation');
    if (!heroEl || !formEl) return;

    // IntersectionObserver работает по реальному layout, а не по абсолютным px —
    // не ломается при смене масштаба страницы (в отличие от scrollY-порогов).
    const heroObserver = new IntersectionObserver(
      ([entry]) => setHeroVisible(entry.isIntersecting),
      { threshold: 0 }
    );
    const formObserver = new IntersectionObserver(
      ([entry]) => setFormVisible(entry.isIntersecting),
      { threshold: 0, rootMargin: '200px 0px 0px 0px' } // считаем форму «видимой» чуть заранее
    );

    heroObserver.observe(heroEl);
    formObserver.observe(formEl);
    return () => {
      heroObserver.disconnect();
      formObserver.disconnect();
    };
  }, []);

  // Фон body = цвет футера, пока открыт лендинг — чтобы при овербаунсе (свайп ниже футера
  // на мобильном, или трекпад-бounce на десктопе) не мелькал белый фон. Возвращаем как было
  // при уходе со страницы, чтобы не задеть админку/кабинет клиента.
  React.useEffect(() => {
    const prevBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#030712';
    return () => { document.body.style.backgroundColor = prevBg; };
  }, []);

  React.useEffect(() => {
    const v = heroVideoRef.current;
    if (!v) return;
    v.muted = true;
    v.defaultMuted = true;
    const playPromise = v.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // Автовоспроизведение заблокировано браузером — пользователь увидит постер, это не ошибка.
      });
    }
  }, []);

  React.useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('visible');
          observer.unobserve(e.target);
        }
      });
    }, { threshold: 0.15 });
    document.querySelectorAll('.why-card-left, .why-card-right, .why-title, .service-item, .step-item').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans">

      {/* NAV */}
      <nav className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-xl border-b border-gray-100 px-5 md:px-12 py-4 md:py-5">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="bg-red-700 p-2 rounded-md text-white shadow">
              <Shield size={22} />
            </div>
            <span className="text-lg font-black text-gray-900 tracking-tighter uppercase italic leading-none">
              ISV <span className="text-red-600">CENTRAL ASIA</span>
            </span>
          </div>
          <div className="hidden md:flex gap-8 items-center">
            <button
              onClick={() => navigate('/news')}
              className="flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-gray-400 hover:text-gray-900 transition-colors"
            >
              <Newspaper size={12} /> {t('LandingPage.nav_news')}
            </button>
            <button
              onClick={() => document.getElementById('consultation')?.scrollIntoView({ behavior: 'smooth' })}
              className="text-xs font-black uppercase tracking-widest text-red-600 hover:text-red-500 transition-colors"
            >
              {t('LandingPage.nav_consultation')}
            </button>
            <button
              onClick={onLoginClick}
              className="text-xs font-black uppercase tracking-widest px-5 py-2.5 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              {t('LandingPage.nav_login')}
            </button>
            <LanguageSwitcher />
          </div>
          {/* Бургер — только на мобильном */}
          <div className="md:hidden flex items-center gap-1">
            <LanguageSwitcher />
            <button
              onClick={() => setMobileMenuOpen(prev => !prev)}
              className="p-2 text-gray-700 hover:text-gray-900 transition-colors"
              aria-label={mobileMenuOpen ? t('LandingPage.close_menu') : t('LandingPage.open_menu')}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Мобильная выпадающая панель */}
        {mobileMenuOpen && (
          <div className="md:hidden flex flex-col gap-1 pt-4 mt-4 border-t border-gray-100">
            <button
              onClick={() => { setMobileMenuOpen(false); navigate('/news'); }}
              className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-gray-500 hover:text-gray-900 transition-colors py-3"
            >
              <Newspaper size={14} /> {t('LandingPage.nav_news')}
            </button>
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                document.getElementById('consultation')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-left text-xs font-black uppercase tracking-widest text-red-600 hover:text-red-500 transition-colors py-3"
            >
              {t('LandingPage.nav_consultation')}
            </button>
            <button
              onClick={() => { setMobileMenuOpen(false); onLoginClick(); }}
              className="text-xs font-black uppercase tracking-widest px-5 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors mt-1 mb-1"
            >
              {t('LandingPage.nav_login')}
            </button>
          </div>
        )}
      </nav>


      {/* HERO */}
      <section id="hero" className="hero-viewport relative flex items-center overflow-hidden">
        <video
          ref={heroVideoRef}
          autoPlay
          muted
          loop
          playsInline
          webkit-playsinline="true"
          preload="auto"
          poster="/isv_video_poster.jpg"
          className="absolute inset-0 w-full h-full object-cover"
          src="/isv_video_optimized.mp4"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 px-5 sm:px-8 pt-24 sm:pt-32 pb-16 sm:pb-24 max-w-3xl">
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-black text-white uppercase italic leading-tight tracking-tight mb-4 sm:mb-6 break-words">
            SOC —<br />
            <span className="text-red-500">{t('LandingPage.hero_title_highlight')}</span>
          </h1>
          <p className="text-base sm:text-lg text-white/80 font-medium leading-relaxed mb-3 sm:mb-4 max-w-2xl">
            {t('LandingPage.hero_subtitle')}
          </p>
          <p className="text-xs sm:text-sm text-white/50 leading-relaxed max-w-2xl">
            {t('LandingPage.hero_description_1')}{' '}
            {t('LandingPage.hero_description_2')}
          </p>
        </div>
        <div className="hidden sm:flex absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex-col items-center gap-2 text-white/30">
          <div className="w-px h-8 bg-white/20" />
          <span className="text-[10px] uppercase tracking-widest">Scroll</span>
        </div>
      </section>

      <style>{`
        .hero-viewport {
          min-height: 100vh;
          min-height: 100svh;
        }
      `}</style>

      {/* WHY SECTION */}
      <section className="bg-white py-24 px-12 overflow-hidden">
        <h2 className="text-3xl font-black text-gray-900 uppercase italic tracking-tight mb-16 why-title">
          {t('LandingPage.why_soc_title')}
        </h2>

        <div className="relative">

          {/* Блок 1 — левый, красный, влетает слева */}
          <div className="why-card-left w-full md:w-5/12 bg-red-700 rounded-2xl p-8">
            <p className="text-xs font-black text-red-300 uppercase tracking-widest mb-4">{t('LandingPage.threat')}</p>
            <h3 className="text-lg font-black text-white uppercase italic leading-snug mb-4">
              {t('LandingPage.cyber_threats_harder')}
            </h3>
            <p className="text-sm text-red-100 leading-relaxed">
              {t('LandingPage.modern_attacks_desc')}
            </p>
          </div>

          {/* Блок 2 — правый, белый, влетает справа, смещён вниз */}
          <div className="why-card-right w-full md:w-5/12 ml-auto mt-8 border border-gray-200 rounded-2xl p-8 bg-white">
            <h3 className="text-base font-black text-gray-900 uppercase italic tracking-tight mb-6">
              {t('LandingPage.main_business_problems')}
            </h3>
            <div className="space-y-5">
              {[
                { title: t('LandingPage.net_kruglosutochnogo_control'), text: t('LandingPage.problem_no_24_7_monitoring') },
                { title: t('LandingPage.shortage_specialists'), text: t('LandingPage.find_train_retain_qualified_analysts') },
                { title: t('LandingPage.high_risks_finansovyh_poter'), text: t('LandingPage.odin_successful_incident_may_lead') },
                { title: t('LandingPage.requirements_regulators'), text: t('LandingPage.faq_monitoring_mandatory_answer') },
              ].map((item, i) => (
                <div key={i} className={i !== 3 ? 'pb-5 border-b border-gray-100' : ''}>
                  <p className="text-sm font-black text-gray-900 mb-1">{item.title}</p>
                  <p className="text-sm text-gray-500 leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      <style>{`
        .why-title {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.5s ease, transform 0.5s ease;
        }
        .why-title.visible { opacity: 1; transform: translateY(0); }

        .why-card-left {
          opacity: 0;
          transform: translateX(-80px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }
        .why-card-left.visible { opacity: 1; transform: translateX(0); }

        .why-card-right {
          opacity: 0;
          transform: translateX(80px);
          transition: opacity 0.6s ease, transform 0.6s ease;
          transition-delay: 0.2s;
        }
        .why-card-right.visible { opacity: 1; transform: translateX(0); }
      `}</style>



      {/* PARTNERS / SERVICES SECTION */}
      <section className="bg-gray-50 py-24 px-12">

        {/* Заголовок — слева */}
        <h2 className="text-3xl font-black text-gray-900 uppercase italic tracking-tight mb-12">
          {t('LandingPage.what_partners_get')}
        </h2>

        {/* Подзаголовок — по центру */}
        <p className="text-xl font-black text-gray-900 italic text-center max-w-2xl mx-auto mb-6 leading-snug">
          {t('LandingPage.full_monitoring_center_no_costs')}
        </p>

        {/* Описание — слева */}
        <p className="text-sm text-gray-500 leading-relaxed max-w-xl mb-12">
          {t('LandingPage.ocib_expertise_no_need_hire')}
        </p>

        {/* В состав услуги входит — один блок */}
        <div className="border border-gray-200 rounded-2xl bg-white overflow-hidden">
          <div className="px-8 py-5 border-b border-gray-100">
            <p className="text-xs font-black text-gray-400 uppercase tracking-widest">{t('LandingPage.sostav_service_vhodit')}</p>
          </div>
          {[
            { title: t('LandingPage.monitoring_events_24_7'), text: t('LandingPage.247_control_activity_information_systems') },
            { title: t('LandingPage.detection_threats_attacks'), text: t('LandingPage.detection_suspicious_activity_before_togo') },
            { title: t('LandingPage.investigation_incidents'), text: t('LandingPage.incident_root_cause_analysis') },
            { title: t('LandingPage.response_incidents'), text: t('LandingPage.preparation_recommendations_support_activitie') },
            { title: t('LandingPage.regulyarnaya_otchetnost'), text: t('LandingPage.providing_analytical_reports_event_security') },
            { title: t('LandingPage.ekspertnaya_support'), text: t('LandingPage.consultation_specialists_topics_information_s') },
          ].map((item, i, arr) => (
            <div
              key={i}
              className={`service-item flex items-start gap-5 px-8 py-5 ${i !== arr.length - 1 ? 'border-b border-gray-100' : ''}`}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              <div className="w-1.5 h-1.5 rounded-full bg-red-600 mt-2 flex-shrink-0" />
              <div>
                <p className="text-sm font-black text-gray-900 mb-0.5">{item.title}</p>
                <p className="text-sm text-gray-500 leading-relaxed">{item.text}</p>
              </div>
            </div>
          ))}
        </div>

        <style>{`
          .service-item {
            opacity: 0;
            transform: scale(0.95);
            transition: opacity 0.5s ease, transform 0.5s ease;
          }
          .service-item.visible {
            opacity: 1;
            transform: scale(1);
          }
        `}</style>

      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white py-24 overflow-hidden">
        <div className="px-12 mb-16">
          <h2 className="text-3xl font-black text-gray-900 uppercase italic tracking-tight mb-3">
            {t('LandingPage.how_soc_works')}
          </h2>
          <p className="text-sm text-gray-400 uppercase tracking-widest font-black">
            {t('LandingPage.simple_interaction_scheme')}
          </p>
        </div>

        <div className="flex flex-col">
          {[
            { n: 1, title: t('LandingPage.connection_sources_events'), text: t('LandingPage.integriruem_infrastructure_zakazchika_systems'), offset: '0%' },
            { n: 2, title: t('LandingPage.sbor_processing_dannyh'), text: t('LandingPage.receive_events_from_various_systems'), offset: '15%' },
            { n: 3, title: t('LandingPage.analysis_correlation'), text: t('LandingPage.auto_detect_suspicious_chains_actions'), offset: '30%' },
            { n: 4, title: t('LandingPage.detection_threats'), text: t('LandingPage.record_signs_attacks_potential_compromise'), offset: '45%' },
            { n: 5, title: t('LandingPage.response'), text: t('LandingPage.notify_responsible_persons_provide_recommenda'), offset: '60%' },
          ].map((step, i) => (
            <div
              key={step.n}
              className="step-item flex items-stretch border-t border-gray-100 last:border-b"
              style={{ '--offset': step.offset, transitionDelay: `${i * 250}ms` }}
            >
              {/* Отступ-ступенька — только на md и выше */}
              <div className="step-offset" style={{ '--offset': step.offset, flexShrink: 0 }} />

              {/* Номер */}
              <div className="flex items-center justify-center w-16 border-r border-gray-100 flex-shrink-0">
              </div>

              {/* Контент */}
              <div className="flex items-center gap-8 px-8 py-6 flex-1">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-red-700 text-white text-sm font-black flex items-center justify-center">
                    {step.n}
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-[10px] font-black text-red-500 uppercase tracking-widest mb-1">{t('LandingPage.step_label')} {step.n}</p>
                  <p className="text-base font-black text-gray-900 uppercase italic mb-1">{step.title}</p>
                  <p className="text-sm text-gray-500 leading-relaxed">{step.text}</p>
                </div>
                {/* Линия до края */}
                <div className="flex-1 h-px bg-gray-100 hidden md:block" />
              </div>
            </div>
          ))}
        </div>

        <style>{`
          .step-item {
            opacity: 0;
            transform: translateX(-60px);
            transition: opacity 0.8s ease, transform 0.8s ease;
          }
          .step-item.visible {
            opacity: 1;
            transform: translateX(0);
          }
          .step-offset {
            width: 0;
          }
          @media (min-width: 768px) {
            .step-offset {
              width: var(--offset);
            }
          }
        `}</style>
      </section>

      {/* WHY ISV */}
      <section className="bg-gray-50 py-24 px-12">

        <h2 className="text-3xl font-black text-gray-900 uppercase italic tracking-tight mb-12">
          {t('LandingPage.why_choose_isv')}
        </h2>

        {/* Три абзаца */}
        <div className="max-w-3xl space-y-6 mb-16">
          <p className="text-sm text-gray-600 leading-relaxed">
            {t('LandingPage.about_company_p1')}
          </p>
          <p className="text-sm text-gray-600 leading-relaxed">
            {t('LandingPage.about_company_p2')}
          </p>
          <p className="text-sm text-gray-500 italic border-l-2 border-red-600 pl-5 leading-relaxed">
            {t('LandingPage.mission_quote')}
          </p>
        </div>

        {/* Ваш партнёр */}
        <p className="text-center text-base font-black text-gray-900 uppercase italic tracking-tight mb-10">
          {t('LandingPage.your_partner_info_security')}
        </p>

        {/* 20+ специалистов — одна строка на десктопе, перенос на мобильном */}
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-2 mb-12">
          <p className="text-6xl font-black text-red-600 leading-none">20+</p>
          <p className="text-base font-black text-gray-900">{t('LandingPage.specialized_specialists')}</p>
          <p className="text-sm text-gray-500 leading-relaxed max-w-sm">
            {t('LandingPage.experts_monitoring_desc')}
          </p>
        </div>

        {/* 4 колонки без карточек и без черточек */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-6 md:gap-y-0">
          {[
            { title: t('LandingPage.prakticheskiy_experience'), text: t('LandingPage.specialists_have_experience_implementation_pr') },
            { title: t('LandingPage.comprehensive_approach'), text: t('LandingPage.detection_threats_before_support_resolution') },
            { title: t('LandingPage.fast_launch'), text: t('LandingPage.connection_nachalo_monitoring_maksimalno_koro') },
            { title: t('LandingPage.ekonomicheskaya_effektivnost'), text: t('LandingPage.cost_service_significantly_lower_costs') },
          ].map((item, i) => (
            <div
              key={i}
              className={`pr-6 ${i % 2 === 0 ? 'pl-0' : 'pl-6 border-l border-gray-200'} md:pl-6 md:border-l md:border-gray-200 ${i === 0 ? 'md:pl-0 md:border-l-0' : ''}`}
            >
              <p className="text-sm font-black text-gray-900 mb-2">{item.title}</p>
              <p className="text-xs text-gray-500 leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>

      </section>

      {/* COMPARE TABLE */}
      <section className="bg-white py-24 px-12">
        <h2 className="text-3xl font-black text-gray-900 uppercase italic tracking-tight mb-12">
          {t('LandingPage.own_soc_or_ocib_title')}
        </h2>
        <div className="border border-gray-200 rounded-2xl overflow-hidden max-w-3xl">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left px-6 py-4 text-sm font-black text-gray-900">{t('LandingPage.parametr')}</th>
                  <th className="text-left px-6 py-4 text-sm font-black text-gray-900">{t('LandingPage.own_soc')}</th>
                  <th className="text-left px-6 py-4 text-sm font-black text-gray-900">{t('LandingPage.soc_isv_central_asia')}</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { param: t('LandingPage.naym_analysts'), soc: t('LandingPage.required'), isv: t('LandingPage.not_required') },
                  { param: t('LandingPage.training_staff'), soc: t('LandingPage.required'), isv: t('LandingPage.not_required') },
                  { param: t('LandingPage.monitoring_24_7'), soc: t('LandingPage.high_costs'), isv: t('LandingPage.vklyucheno') },
                  { param: t('LandingPage.ekspertiza'), soc: t('LandingPage.built_over_years'), isv: t('LandingPage.available_srazu') },
                  { param: t('LandingPage.speed_launch'), soc: t('LandingPage.several_months'), isv: t('LandingPage.neskolkih_weeks') },
                  { param: t('LandingPage.cost'), soc: t('LandingPage.high'), isv: t('LandingPage.optimizirovannaya') },
                ].map((row, i, arr) => (
                  <tr key={row.param} className={i !== arr.length - 1 ? 'border-b border-gray-100' : ''}>
                    <td className="px-6 py-4 text-gray-700 font-medium">{row.param}</td>
                    <td className="px-6 py-4 text-gray-500">{row.soc}</td>
                    <td className="px-6 py-4">
                      <span className="text-green-600 font-black">{row.isv}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Кому подходит */}
        <div className="mt-16">
          <h3 className="text-xl font-black text-gray-900 uppercase italic tracking-tight mb-3">
            {t('LandingPage.who_fits_ocib')}
          </h3>
          <p className="text-sm text-gray-500 leading-relaxed mb-6 max-w-2xl">
            {t('LandingPage.our_service_demanded_desc')}
          </p>
          <div className="flex flex-wrap gap-3">
            {[t('LandingPage.government_organization'), t('LandingPage.financial_sector'), t('LandingPage.proizvodstvennye_predpriyatiya'), t('LandingPage.telecom_company'), t('LandingPage.medical_institutions'), t('LandingPage.riteyl_e_commerce'), t('LandingPage.logistics_company')].map(item => (
              <span key={item} className="who-tag px-4 py-2 border border-gray-200 rounded-full text-sm text-gray-600 font-medium bg-white cursor-default transition-all duration-300 hover:border-red-500 hover:text-red-600 hover:shadow-sm hover:shadow-red-100">
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS — бесконечная лента */}
      <section className="bg-gray-50 py-16 overflow-hidden">
        <div className="px-12 mb-10">
          <h2 className="text-3xl font-black text-gray-900 uppercase italic tracking-tight">
            {t('LandingPage.our_partners_clients')}
          </h2>
        </div>
        <style>{`
          @keyframes ticker {
            0%   { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .ticker-track {
            display: flex;
            width: max-content;
            animation: ticker 32s linear infinite;
          }
          .ticker-track:hover { animation-play-state: paused; }
        `}</style>
        <div className="relative">
          {/* fade edges */}
          <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-8 sm:w-24 z-10" style={{background: 'linear-gradient(to right, #f9fafb, transparent)'}} />
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 sm:w-24 z-10" style={{background: 'linear-gradient(to left, #f9fafb, transparent)'}} />
          <div className="ticker-track">
            {(() => {
              const partners = [
                { logo: '/assets/logos/image1.png',  name: 'Astana IT University' },
                { logo: '/assets/logos/image2.png',  name: t('LandingPage.service_rescue_city_almaty') },
                { logo: '/assets/logos/image3.png',  name: t('LandingPage.agency_financial_monitoring_rk') },
                { logo: '/assets/logos/image4.png',  name: t('LandingPage.traffic_control_center_astana') },
                { logo: '/assets/logos/image5.png',  name: t('LandingPage.rgp_nats_center_geodesy_spatial') },
                { logo: '/assets/logos/image7.jpg',  name: t('LandingPage.national_center_independent_examination') },
                { logo: '/assets/logos/image8.png',  name: t('LandingPage.eksportno_credit_agency_kazahstana') },
                { logo: '/assets/logos/image9.png',  name: t('LandingPage.rgp_nats_center_quality_road') },
                { logo: '/assets/logos/image10.png', name: t('LandingPage.generalnaya_prokuratura_respubliki_kazahstan') },
                { logo: '/assets/logos/image11.jpg', name: t('LandingPage.ao_shinhan_bank_kazahstan') },
                { logo: '/assets/logos/image12.png', name: t('LandingPage.ao_trade_industrial_bank_china') },
                { logo: '/assets/logos/image13.jfif',name: t('LandingPage.ministry_trade_integration_rk') },
              ];
              /* дублируем для бесшовного цикла */
              return [...partners, ...partners].map((p, i) => (
                <div
                  key={i}
                  className="flex-shrink-0 mx-4 flex items-center gap-5 bg-white border border-gray-200 rounded-2xl px-6 py-5 hover:border-red-500/50 hover:shadow-md transition-all duration-200 cursor-default"
                  style={{ minWidth: '340px', maxWidth: '390px' }}
                >
                  <div className="w-20 h-20 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden p-2">
                    <img
                      src={p.logo}
                      alt={p.name}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        if (!e.target.dataset.retried) {
                          e.target.dataset.retried = '1';
                          const src = e.target.src;
                          setTimeout(() => { e.target.src = src; }, 800);
                          return;
                        }
                        e.target.style.display = 'none';
                        e.target.parentNode.classList.add('bg-gray-100');
                      }}
                    />
                  </div>
                  <p className="text-sm font-bold text-gray-700 leading-snug">{p.name}</p>
                </div>
              ));
            })()}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-24 px-12">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-black text-gray-900 uppercase italic tracking-tight mb-12 text-center">
            {t('LandingPage.faq_title')}
          </h2>
          <div className="divide-y divide-gray-100">
            {[
              { q: t('LandingPage.what_takoe_soc_soc'), a: t('LandingPage.soc_soc_this_center_monitoring') },
              { q: t('LandingPage.than_your_soc_differs_siem'), a: t('LandingPage.siem_this_instrument_for_sbora') },
              { q: t('LandingPage.need_whether_us_soc_if'), a: t('LandingPage.da_antivirus_firewall_screen_are') },
              { q: t('LandingPage.how_bystro_can_connect_service'), a: t('LandingPage.term_depends_number_systems_complexity') },
              { q: t('LandingPage.what_happens_during_detection_incident'), a: t('LandingPage.our_specialists_analiziruyut_event_opredelyay') },
              { q: t('LandingPage.rabotaet_whether_soc_kruglosutochno'), a: t('LandingPage.da_monitoring_processing_events_carriedout') },
              { q: t('LandingPage.need_whether_have_sobstvennogo_specialist'), a: t('LandingPage.net_soc_allows_get_dostup') },
              { q: t('LandingPage.skolko_stoit_soc'), a: t('LandingPage.cost_depends_number_podklyuchaemyh_systems') },
              { q: t('LandingPage.what_willbe_if_kiberataka_proizoydet'), a: t('LandingPage.without_kruglosutochnogo_monitoring_incident') },
            ].map((item, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={i} className="py-5 cursor-pointer" onClick={() => setOpenFaq(isOpen ? null : i)}>
                  <div className="flex justify-between items-center gap-4">
                    <p className={`text-sm font-black transition-colors duration-200 ${isOpen ? 'text-red-600' : 'text-gray-900 hover:text-red-600'}`}>
                      {item.q}
                    </p>
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full border grid place-items-center transition-all duration-300 ${isOpen ? 'border-red-500 text-red-500 rotate-45' : 'border-gray-300 text-gray-500'}`}
                      style={{fontSize:'14px', lineHeight:1}}>
                      +
                    </div>
                  </div>
                  <div className={`faq-answer overflow-hidden transition-all duration-400 ease-in-out ${isOpen ? 'max-h-48 opacity-100 mt-3' : 'max-h-0 opacity-0'}`}
                    style={{ transition: 'max-height 0.4s ease, opacity 0.3s ease, margin-top 0.3s ease' }}
                  >
                    <p className="text-sm text-gray-500 leading-relaxed">{item.a}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CERTIFICATES */}
      {/* CERTIFICATES — бесконечная лента */}
      <section id="Сертификаты" className="bg-gray-50 py-16 overflow-hidden">
        <div className="px-12 mb-10">
          <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2">International Security Accreditation</p>
          <h2 className="text-3xl font-black text-gray-900 uppercase italic tracking-tight">
            {t('LandingPage.our_certificates')}
          </h2>
        </div>
        <div className="relative">
          <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-8 sm:w-24 z-10" style={{background: 'linear-gradient(to right, #f9fafb, transparent)'}} />
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 sm:w-24 z-10" style={{background: 'linear-gradient(to left, #f9fafb, transparent)'}} />
          <div className="ticker-track">
            {(() => {
              const certs = [
                { logo: '/assets/certs/cissp.png',   name: 'CISSP',     full: 'Certified Information Systems Security Professional' },
                { logo: '/assets/certs/cism.png',    name: 'CISM',      full: 'Certified Information Security Manager' },
                { logo: '/assets/certs/iso27001.jpg',name: 'ISO 27001', full: 'Lead Auditor' },
                { logo: '/assets/certs/oscp.png',    name: 'OSCP+',     full: 'Offensive Security Certified Professional' },
                { logo: '/assets/certs/CEH.png',     name: 'CEH',       full: 'Certified Ethical Hacker' },
                { logo: '/assets/certs/ECIH.png',    name: 'ECIH',      full: 'EC-Council Certified Incident Handler' },
                { logo: '/assets/certs/ECSA.png',    name: 'ECSA',      full: 'EC-Council Certified Security Analyst' },
                { logo: '/assets/certs/CHFI.png',    name: 'CHFI',      full: 'Computer Hacking Forensic Investigator' },
                { logo: '/assets/certs/aws_saa.png', name: 'AWS SAA',   full: 'AWS Solutions Architect Associate' },
              ];
              return [...certs, ...certs].map((c, i) => (
                <div
                  key={i}
                  className="flex-shrink-0 mx-4 flex flex-col items-center justify-center gap-3 bg-white border border-gray-200 rounded-2xl px-6 py-5 hover:border-red-500/50 hover:shadow-md transition-all duration-200 cursor-default"
                  style={{ minWidth: '160px', width: '160px' }}
                >
                  <div className="w-20 h-14 flex items-center justify-center p-1">
                    <img
                      src={c.logo}
                      alt={c.name}
                      className="max-w-full max-h-full object-contain"
                      style={{ maxWidth: '80px', maxHeight: '56px' }}
                      onError={(e) => {
                        if (!e.target.dataset.retried) {
                          e.target.dataset.retried = '1';
                          const src = e.target.src;
                          setTimeout(() => { e.target.src = src; }, 800);
                          return;
                        }
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                  <p className="text-xs font-black text-gray-900 uppercase tracking-widest text-center">{c.name}</p>
                  <p className="text-[10px] text-gray-400 text-center leading-tight">{c.full}</p>
                </div>
              ));
            })()}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section id="consultation" className="bg-gray-900 py-24 px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-start max-w-5xl mx-auto">
          {/* Левая часть */}
          <div>
            <p className="text-xs font-black text-gray-500 uppercase tracking-widest mb-4">{t('LandingPage.get_consultation')}</p>
            <h2 className="text-3xl font-black text-white uppercase italic tracking-tight leading-tight mb-4">
              {t('LandingPage.get_consultation_connect_ocib')}
            </h2>
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              {t('LandingPage.specialists_will_analyze_infra')}
            </p>
            <p className="text-sm text-gray-500 leading-relaxed">
              {t('LandingPage.leave_request_get_consultation')}
            </p>
          </div>

          {/* Правая — рабочая форма */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
            {formStatus === 'success' ? (
              <div className="flex flex-col items-center justify-center gap-5 py-8 text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <div>
                  <h4 className="text-lg font-black text-white uppercase italic mb-1">{t('LandingPage.application_prinyata')}</h4>
                  <p className="text-sm text-gray-400">{t('LandingPage.our_specialists_contact_you_nearest')}</p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {/* Имя — обязательное */}
                <div>
                  <input
                    type="text"
                    placeholder={t('LandingPage.name')}
                    maxLength={50}
                    value={form.name}
                    onChange={e => handleFormChange('name', e.target.value)}
                    className={`w-full bg-white/5 border rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 outline-none focus:border-red-600 transition-colors ${formErrors.name ? 'border-red-500' : 'border-white/10'}`}
                  />
                  {formErrors.name && <p className="text-xs text-red-400 mt-1 pl-1">{formErrors.name}</p>}
                </div>
                {/* Компания */}
                <input
                  type="text"
                  placeholder={t('LandingPage.company')}
                  maxLength={100}
                  value={form.company}
                  onChange={e => handleFormChange('company', e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 outline-none focus:border-red-600 transition-colors"
                />
                {/* Должность */}
                <input
                  type="text"
                  placeholder={t('LandingPage.position')}
                  maxLength={50}
                  value={form.position}
                  onChange={e => handleFormChange('position', e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 outline-none focus:border-red-600 transition-colors"
                />
                {/* Телефон — обязательное */}
                <div>
                  <input
                    type="tel"
                    placeholder="+7 (7XX) XXX-XX-XX *"
                    value={form.phone}
                    onChange={e => {
                      // разрешаем только цифры, +, -, пробелы, скобки
                      const val = e.target.value.replace(/[^\d+\-()\s]/g, '');
                      // не более 16 символов (достаточно для +7 (700) 000-00-00)
                      if (val.replace(/\D/g, '').length <= 12) handleFormChange('phone', val);
                    }}
                    maxLength={18}
                    className={`w-full bg-white/5 border rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 outline-none focus:border-red-600 transition-colors ${formErrors.phone ? 'border-red-500' : 'border-white/10'}`}
                  />
                  {formErrors.phone && <p className="text-xs text-red-400 mt-1 pl-1">{formErrors.phone}</p>}
                </div>
                {/* Email — обязательное */}
                <div>
                  <input
                    type="email"
                    placeholder={t('LandingPage.email')}
                    maxLength={100}
                    value={form.email}
                    onChange={e => handleFormChange('email', e.target.value)}
                    className={`w-full bg-white/5 border rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 outline-none focus:border-red-600 transition-colors ${formErrors.email ? 'border-red-500' : 'border-white/10'}`}
                  />
                  {formErrors.email && <p className="text-xs text-red-400 mt-1 pl-1">{formErrors.email}</p>}
                </div>
                {/* Вопрос — необязательное */}
                <textarea
                  placeholder={t('LandingPage.your_question')}
                  maxLength={1000}
                  rows={3}
                  value={form.question}
                  onChange={e => handleFormChange('question', e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 outline-none focus:border-red-600 transition-colors resize-none"
                />
                {/* Ошибка сервера */}
                {formStatus === 'error' && formServerError && (
                  <p className="text-xs text-red-400 text-center">{formServerError}</p>
                )}
                <button
                  onClick={handleFormSubmit}
                  disabled={formStatus === 'loading'}
                  className="w-full bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white text-sm font-black uppercase tracking-widest py-3.5 rounded-xl transition-colors mt-1"
                >
                  {formStatus === 'loading' ? t('LandingPage.sending') : t('LandingPage.ostavit_application')}
                </button>
                <p className="text-xs text-gray-600 text-center">{t('LandingPage.required_fields')}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <div className="bg-gray-950 px-12 py-6 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="bg-red-700 p-1.5 rounded-md">
            <Shield size={16} className="text-white" />
          </div>
          <span className="text-xs font-black text-gray-500 uppercase tracking-widest">ISV Central Asia</span>
        </div>
        <p className="text-xs text-gray-600 uppercase tracking-widest">{t('LandingPage.soc_astana_kazahstan_2016')}</p>
      </div>

      {/* Плавающая кнопка → форма заявки */}
      <button
        onClick={scrollToConsultation}
        aria-label={t('LandingPage.get_consultation')}
        className={`fixed bottom-28 right-6 z-40 flex items-center gap-2 bg-red-700 hover:bg-red-600 text-white text-xs font-black uppercase tracking-widest pl-5 pr-4 py-3.5 rounded-full shadow-xl shadow-red-900/30 transition-all duration-300 ${showFab ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'}`}
      >
        {t('LandingPage.ostavit_application')}
        <ArrowUpRight size={16} />
      </button>

      {/* Modals */}
      <ContactModal isOpen={!!activeModal} onClose={() => setActiveModal(null)} title={t('LandingPage.contact_soc')} />
    </div>
  );
};
